/* screens/academy/CertificatePage.jsx — ПУБЛИЧНАЯ страница сертификата
   (/certificate/:publicId). Открывается БЕЗ авторизации: данные тянем по
   неугадываемому public_id через AllowAny-эндпоинт.

   ЮРИДИЧЕСКОЕ (ФЗ-273): это «Сертификат о прохождении курса» — документ
   информационного характера, НЕ документ об образовании (поэтому без
   образовательной лицензии). Поэтому: заголовок «Сертификат» (не «диплом»),
   формулировка про факт прохождения, обязательная пометка «Не является
   документом об образовании». Подпись — Опескин К.Е. (снимок из настроек).

   CSS-классы остались с префиксом .dpl-* (styles/diploma.css) — это внутренние
   имена, на смысл документа не влияют. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/academy.js';

const { useFakeQuery } = hooks;
const { useParams, useNavigate } = RR;

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ru-RU',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

// печать (SVG-кружок с текстом по окружности + звезда + год)
function Seal({ year }) {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <path id="dpl-seal-arc" d="M 16,50 A 34,34 0 0 1 84,50" fill="none" />
      </defs>
      <circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="50" cy="50" r="41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity=".5" />
      {/* Размер и разрядка подобраны так, чтобы строка ГАРАНТИРОВАННО влезала в
          дугу: полуокружность r=34 даёт ≈107 единиц длины, а прежние 8.4px с
          разрядкой 1.1 требовали ~135 — печать выходила обрезанной с обоих
          концов и читалась «АДЕМИЯ · РЕСТОРИУ». Заметно это только на готовом
          документе, который ученик показывает работодателю, поэтому запас взят
          с ~10 % на разницу метрик шрифта между браузерами. */}
      <text fontSize="6.2" fontWeight="700" letterSpacing="0.4" fill="currentColor" textAnchor="middle">
        <textPath href="#dpl-seal-arc" xlinkHref="#dpl-seal-arc" startOffset="50%">
          АКАДЕМИЯ · РЕСТОРИУМ
        </textPath>
      </text>
      <path d="M50 35 L52.5 47.5 L65 50 L52.5 52.5 L50 65 L47.5 52.5 L35 50 L47.5 47.5 Z"
        fill="currentColor" />
      {year && (
        <text x="50" y="75" fontSize="6.8" fontWeight="700" letterSpacing="1"
          fill="currentColor" textAnchor="middle">{year}</text>
      )}
    </svg>
  );
}

function Sheet({ cert }) {
  const year = cert.issued_at ? String(new Date(cert.issued_at).getFullYear()) : '';
  return (
    <div className="dpl-sheet" role="img"
      aria-label={`Сертификат ${cert.serial}: ${cert.recipient_name} — курс «${cert.course_title}»`}>
      <div className="dpl-frame" aria-hidden="true">
        <span className="dpl-corner dpl-corner--tl" />
        <span className="dpl-corner dpl-corner--tr" />
        <span className="dpl-corner dpl-corner--bl" />
        <span className="dpl-corner dpl-corner--br" />
      </div>
      <div className="dpl-inner">
        <div className="dpl-eyebrow">
          <span className="dpl-eyebrow__mark"><Icon name="sparkles" size={12} /></span>
          Академия Ресториум
        </div>
        <h1 className="dpl-title">Сертификат</h1>
        <div className="dpl-title__under" aria-hidden="true" />

        <div className="dpl-sub">настоящим подтверждается, что</div>
        <div className="dpl-name">{cert.recipient_name}</div>
        <div className="dpl-name__rule" aria-hidden="true" />

        <div className="dpl-text">прошёл(-ла) обучающий онлайн-курс</div>
        <div className="dpl-course">«{cert.course_title}»</div>
        {cert.course_summary && <div className="dpl-course-sub">{cert.course_summary}</div>}

        <div className="dpl-foot">
          <div className="dpl-foot__col">
            <div className="dpl-foot__val">{cert.serial || '—'}</div>
            <div className="dpl-foot__label">Номер сертификата</div>
            <div className="dpl-foot__val">{fmtDate(cert.issued_at)}</div>
            <div className="dpl-foot__label">Дата выдачи</div>
          </div>

          <div className="dpl-seal"><Seal year={year} /></div>

          <div className="dpl-foot__col dpl-foot__col--sign">
            <div className="dpl-sign__name">{cert.signer_name}</div>
            <div className="dpl-sign__rule" aria-hidden="true" />
            <div className="dpl-sign__title">{cert.signer_title}</div>
          </div>
        </div>

        {/* юридическая пометка (печатается вместе с документом) */}
        <div className="dpl-note">
          Подтверждает прохождение онлайн-курса Академии Ресториум.
          Не является документом об образовании.
        </div>
      </div>
    </div>
  );
}

function CertificatePage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useFakeQuery('academy-cert-' + publicId, () => api.getCertificate(publicId), [publicId]);
  const cert = q.data;
  const [saving, setSaving] = React.useState(false);

  /* ST-14. До этого «скачать» умел только window.print(): файла как артефакта не
     существовало, вид зависел от браузера, приложить документ к резюме одним
     кликом было нельзя. Теперь PDF собирает сервер, а печать остаётся отдельной
     кнопкой — это разные действия, и обещать их одной больше нечестно. */
  const savePdf = async () => {
    setSaving(true);
    try {
      const r = await api.downloadCertificatePdf(publicId);
      if (!r.ok) {
        // downloadFile не бросает: без разбора ответа отказ выглядел бы как
        // «ничего не произошло» — самый неприятный вид отказа. Тон именно
        // 'danger': у токена 'error' в теме нет стилей, и провал выглядел бы
        // такой же нейтральной плашкой, как «ссылка скопирована».
        toast.show((r.data && r.data.detail) || 'Не удалось скачать PDF сертификата',
          'danger');
      }
    } catch {
      toast.show('Не удалось скачать PDF сертификата', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    const url = (typeof window !== 'undefined')
      ? `${window.location.origin}/certificate/${publicId}` : '';
    try {
      await navigator.clipboard.writeText(url);
      toast.show('Ссылка на сертификат скопирована', 'success');
    } catch {
      toast.show(url, 'info');
    }
  };

  return (
    <div className="dpl-page">
      <header className="dpl-bar no-print">
        <Button variant="ghost" size="sm" iconLeft="arrow-left"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/learning'))}>
          Назад
        </Button>
        <span className="dpl-bar__spacer" />
        <span className="dpl-bar__brand">
          <img src="/logo-mark.svg" alt="" width={22} height={22} />Ресториум
        </span>
        <span className="dpl-bar__spacer" />
        {cert && (
          <div className="dpl-bar__actions">
            <Button variant="secondary" size="sm" iconLeft="copy" onClick={copyLink}>Ссылка</Button>
            {/* «Печать» без иконки: реестр Icon принтера не знает, а download здесь
                врал — печать файла не даёт. Ширины трёх кнопок хватает на 380 px. */}
            <Button variant="secondary" size="sm"
              onClick={() => window.print()}>Печать</Button>
            <Button variant="primary" size="sm" iconLeft="download" loading={saving}
              onClick={savePdf}>Скачать PDF</Button>
          </div>
        )}
      </header>

      {q.loading && !cert && (
        <div className="dpl-state">
          <div className="dpl-state__glyph"><Icon name="sparkles" size={22} /></div>
          <div className="t-label">Загружаем сертификат…</div>
        </div>
      )}

      {!q.loading && (q.error || !cert) && (
        <div className="dpl-state">
          <div className="dpl-state__glyph"><Icon name="alert-circle" size={22} /></div>
          <h2 className="t-h3">Сертификат не найден</h2>
          <p>Ссылка недействительна или сертификат был аннулирован.</p>
          <Button variant="primary" onClick={() => navigate('/learning')}>В обучение</Button>
        </div>
      )}

      {cert && (
        <main className="dpl-main">
          <Sheet cert={cert} />
          <div className="dpl-verify no-print">
            <Icon name="check-circle" size={15} />
            Эту ссылку можно отправить кому угодно — сертификат откроется без входа.
          </div>
        </main>
      )}
    </div>
  );
}

export { CertificatePage };
